// qs-LayerSection.vue
<template>
  <div class="layer-section">
    <div class="section-content">
      <button
        :disabled="isAddingLayer"
        class="add-layer-btn"
        @click="handleAddLayer"
      >
        <div class="flex items-center gap-2">
          <Plus />
          {{ isAddingLayer ? 'Adding...' : 'Add Layer' }}
        </div>
      </button>
      <div class="layers-list">
        <TransitionGroup
          name="layer"
          tag="div"
        >
          <LayerItem
            v-for="(layer, index) in sortedLayers"
            :key="layer.id"
            :layer="layer"
            :index="index"
            :is-selected="selectedLayerId === layer.id"
            :is-processing="isProcessing"
            :can-delete="canDeleteLayer"
            :total-layers="sortedLayers.length"
            @layer-click="handleLayerClick"
            @toggle-visibility="handleToggleVisibility"
            @toggle-lock="handleToggleLock"
            @duplicate="handleDuplicateLayer"
            @delete="handleDeleteLayer"
            @move="handleLayerPosition"
            @start-rename="handleStartRename"
            @finish-rename="handleFinishRename"
            @cancel-rename="handleCancelRename"
            @move-up="handleMoveUp"
            @move-down="handleMoveDown"
            @move-to-top="handleMoveToTop"
            @move-to-bottom="handleMoveToBottom"
          />
        </TransitionGroup>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Plus } from 'lucide-vue-next'
import LayerItem from '@/components/panels/qs-LayerItem.vue'
import { useLayerStore } from '@/stores/qs-store-layers'
import type { Layer } from '@/types/qs-types-index'

const emit = defineEmits<{
  'layer-selected': [layerId: string]
  'layer-added': [layer: Layer]
  'layer-duplicated': [layer: Layer]
  'layer-deleted': [layerId: string]
  'layer-move': [layerId: string]
  'layer-visibility-changed': [layerId: string, visible: boolean]
  'layer-lock-changed': [layerId: string, locked: boolean]
  'layer-renamed': [layerId: string, newName: string]
  'layer-reordered': [fromIndex: number, toIndex: number]
}>()

const layerStore = useLayerStore()

const editingLayerId = ref<string | null>(null)
const isAddingLayer = ref(false)
const isProcessing = ref(false)

const layers = computed(() => layerStore.layers)
const sortedLayers = computed(() => layerStore.layersInOrder)
const canDeleteLayer = computed(() => layers.value.length > 1)
const selectedLayerId = computed(() => layerStore.currentLayerId)

const handleLayerClick = (layerId: string): void => {
  if (editingLayerId.value) return
  const success = layerStore.setCurrentLayer(layerId)
  if (!success) return
  emit('layer-selected', layerId)
}

const handleAddLayer = async (): Promise<void> => {
  if (isAddingLayer.value) return
  isAddingLayer.value = true
  try {
    const newLayer = layerStore.createLayer()
    emit('layer-added', newLayer)
  } catch (error) {
    console.error('Failed to add layer:', error)
  } finally {
    isAddingLayer.value = false
  }
}

const handleToggleVisibility = (layerId: string): void => {
  if (isProcessing.value) return
  isProcessing.value = true
  try {
    const layer = layerStore.layers.find(l => l.id === layerId)
    if (layer) {
      layerStore.toggleLayerVisibility(layerId)
      emit('layer-visibility-changed', layerId, layer.visible)
    }
  } catch (error) {
    console.error('Failed to toggle visibility:', error)
  } finally {
    isProcessing.value = false
  }
}

const handleToggleLock = (layerId: string): void => {
  if (isProcessing.value) return
  isProcessing.value = true
  try {
    const layer = layerStore.layers.find(l => l.id === layerId)
    if (layer) {
      layerStore.toggleLayerLock(layerId)
      emit('layer-lock-changed', layerId, layer.locked)
    }
  } catch (error) {
    console.error('Failed to toggle lock:', error)
  } finally {
    isProcessing.value = false
  }
}

const handleDuplicateLayer = (layerId: string): void => {
  if (isProcessing.value) return
  isProcessing.value = true
  try {
    const duplicatedLayer = layerStore.duplicateLayer(layerId)
    if (duplicatedLayer) {
      emit('layer-duplicated', duplicatedLayer)
    }
  } catch (error) {
    console.error('Failed to duplicate layer:', error)
  } finally {
    isProcessing.value = false
  }
}

const handleDeleteLayer = (layerId: string): void => {
  if (!canDeleteLayer.value || isProcessing.value) return
  const layer = layerStore.layers.find(l => l.id === layerId)
  if (layer?.locked) return
  isProcessing.value = true
  try {
    layerStore.deleteLayer(layerId)
    if (layerStore.currentLayerId === layerId) {
      const newCurrentLayerId = layers.value[0]?.id ?? null
      if (newCurrentLayerId) {
        layerStore.setCurrentLayer(newCurrentLayerId)
      }
    }
    emit('layer-deleted', layerId)
  } catch (error) {
    console.error('Failed to delete layer:', error)
  } finally {
    isProcessing.value = false
  }
}

const handleLayerPosition = (layerId: string): void => {
  if (isProcessing.value) return
  emit('layer-move', layerId)
}

const handleStartRename = (layerId: string): void => {
  editingLayerId.value = layerId
}

const handleFinishRename = (layerId: string, newName: string): void => {
  try {
    layerStore.renameLayer(layerId, newName)
    emit('layer-renamed', layerId, newName)
  } catch (error) {
    console.error('Failed to rename layer:', error)
  } finally {
    editingLayerId.value = null
  }
}

const handleCancelRename = (): void => {
  editingLayerId.value = null
}

const handleMoveUp = (_layerId: string, index: number): void => {
  if (isProcessing.value) return
  const newIndex = index - 1
  const success = layerStore.reorderLayers(index, newIndex)
  if (success) {
    emit('layer-reordered', index, newIndex)
  }
}

const handleMoveDown = (_layerId: string, index: number): void => {
  if (isProcessing.value) return
  const newIndex = index + 1
  const success = layerStore.reorderLayers(index, newIndex)
  if (success) {
    emit('layer-reordered', index, newIndex)
  }
}

const handleMoveToTop = (_layerId: string, index: number): void => {
  if (isProcessing.value) return
  const newIndex = 0
  const success = layerStore.reorderLayers(index, newIndex)
  if (success) {
    emit('layer-reordered', index, newIndex)
  }
}

const handleMoveToBottom = (_layerId: string, index: number): void => {
  if (isProcessing.value) return
  const newIndex = sortedLayers.value.length - 1
  const success = layerStore.reorderLayers(index, newIndex)
  if (success) {
    emit('layer-reordered', index, newIndex)
  }
}
</script>

<style scoped>
.layer-section {
  width: 100%;
  background: transparent;
  overflow: visible;
}

.section-content {
  background: transparent;
  border-radius: 0;
  border: none;
}

.add-layer-btn {
  width: 100%;
  height: 36px;
  padding: 0 12px;
  background-color: theme('colors.creme.DEFAULT');
  color: theme('colors.text.DEFAULT');
  border: none;
  border-radius: 0;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 12px;
  font-weight: 500;
  outline: none;
}

.add-layer-btn:hover:not(:disabled) {
  background-color: theme('colors.creme.medium');
}

.add-layer-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.layers-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.layer-enter-active,
.layer-leave-active {
  transition: all 0.3s ease;
}

.layer-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.layer-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.layer-move {
  transition: transform 0.3s ease;
}
</style>